import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import {
	Drawer,
	Button,
	Input,
	Spin,
	Divider,
	InputNumber,
	Switch,
} from "antd";
// FONT AWESOME LIBRYARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuildingShield } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { usePutBankMutation } from "../../../redux/banks/bankAPI";
import {
	updateOpenModalEditBank,
	updateBankAlert,
} from "../../../redux/banks/bankSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";

function BanksEditForm() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [isLoading, setIsLoading] = useState(false);
	const [name, setName] = useState("");
	const [board, setBoard] = useState(0);
	const [active, setActive] = useState(true);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { bank, openModalEditBank } = useSelector((state: any) => state.bank);
	const { theme } = useSelector((state: any) => state.home);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		putBank,
		{
			isSuccess: isSuccessPutBank,
			isError: isErrorPutBank,
			error: errorPutBank,
			isLoading: isLoadingPutBank,
			reset: resetPutBank,
		},
	] = usePutBankMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const showIsLoading = () => {
		setIsLoading(true);
		setTimeout(() => {
			setIsLoading(false);
		}, 1000);
	};

	const onFill = () => {
		setName(bank.name);
		setBoard(bank.board);
		setActive(bank.active);
	};

	const onChangeName = (newValue: string) => {
		setName(newValue);
	};

	const onChangeBoard = (newValue: number) => {
		setBoard(newValue);
	};

	const handleCloseModal = () => {
		dispatch(updateOpenModalEditBank(false));
	};

	const onChangeActive = (newValue: boolean) => {
		setActive(newValue);
	};

	const onClickSave = async () => {
		const token = await GETJwtToken();
		const copy = structuredClone(bank);
		delete copy.data_blocks;
		delete copy.lifts;
		const BODY = {
			token,
			id: bank.id,
			body: {
				...copy,
				control_panel: JSON.stringify(copy.control_panel),
				name,
				board,
				active,
			},
		};
		putBank(BODY);
	};

	const onClose = () => {
		dispatch(updateOpenModalEditBank(false));
	};

	const disableSaveButton = () => {
		let disableButton = false;
		if (name === "" || board > 99999) {
			disableButton = true;
		}
		return disableButton;
	};

	// ************************************************* */
	// USE EFFECT ************************************** */

	useEffect(() => {
		showIsLoading();
		if (openModalEditBank) {
			resetPutBank();
			if (bank) {
				onFill();
			}
		}
	}, [openModalEditBank]);

	useEffect(() => {
		if (isSuccessPutBank) {
			setTimeout(() => {
				dispatch(
					updateBankAlert({
						title: t("general.success"),
						description: t("edgeManagement.banks.bankEdited"),
						status: "success",
					})
				);
			}, 150);
			dispatch(updateOpenModalEditBank(false));
		}
		if (isErrorPutBank) {
			setTimeout(() => {
				dispatch(
					updateBankAlert({
						title: t("general.error"),
						description: errorPutBank,
						status: "error",
					})
				);
			}, 150);
			dispatch(updateOpenModalEditBank(false));
		}
	}, [isSuccessPutBank, isErrorPutBank]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Drawer
			width='35%'
			placement='right'
			onClose={onClose}
			closable={false}
			open={openModalEditBank}
		>
			<Spin spinning={isLoadingPutBank || isLoading} tip={t("general.loading")}>
				{/** ---------------------------------------------------- */}
				{/** BANK INFORMATION */}
				<div>
					<Divider orientation='left' className='generalStyles__drawerDivider'>
						<h5>
							<FontAwesomeIcon
								icon={faBuildingShield}
								className='generalStyles__info generalStyles__mrFix'
							/>
							{t("edgeManagement.banks.editBank")}
						</h5>
					</Divider>
				</div>
				<div className='container'>
					<div className={`drawer__box__${theme} row`}>
						{/** LIFT TYPE */}
						<div className='col-md-12 mt-3'>
							<div className='generalStyles__flex'>
								<div className='generalStyles__inputFlexName'>
									<span className='generalStyles__inputFlexRequired'>*</span>
									<span>{t("edgeManagement.lifts.type")}</span>
								</div>
								<div className='generalStyles__width100'>
									<Input value='ELEVATOR' disabled />
								</div>
								<div className='generalStyles__infoTooltipIconBlank' />
							</div>
						</div>
						{/** NAME */}
						<div className='col-md-12 mt-3'>
							<div className='generalStyles__flex'>
								<div className='generalStyles__inputFlexName'>
									<span className='generalStyles__inputFlexRequired'>*</span>
									<span className='drawer__text'>
										{t("edgeManagement.banks.bankName")}
									</span>
								</div>
								<div className='generalStyles__width100'>
									<Input
										className=''
										size='small'
										value={name}
										onChange={(value) => {
											onChangeName(value.target.value);
										}}
									/>
								</div>
								<div className='generalStyles__infoTooltipIconBlank' />
							</div>
						</div>
						{/** BOARD */}
						<div className='col-md-12 mt-3'>
							<div className='generalStyles__flex'>
								<div className='generalStyles__inputFlexName'>
									<span className='generalStyles__inputFlexRequired'>*</span>
									<span>{t("configuration.configurationFiles.board")}</span>
								</div>
								<div className='generalStyles__width100'>
									<InputNumber
										className='generalStyles__inputNumberFull'
										precision={0}
										value={board}
										onChange={(e: any) => {
											onChangeBoard(e);
										}}
										max={99999}
										size='small'
									/>
								</div>
								<div className='generalStyles__infoTooltipIconBlank' />{" "}
							</div>
						</div>
						{/** BANK ACTIVE */}
						<div className='col-md-12 mt-3'>
							<div className='generalStyles__flex'>
								<div className='generalStyles__inputFlexName'>
									<span>{t("general.active")}</span>
								</div>
								<div className='generalStyles__width100'>
									<Switch onChange={onChangeActive} checked={active} />
								</div>
								<div className='generalStyles__infoTooltipIconBlank' />
							</div>
						</div>
					</div>
					<br />
				</div>
				{/** ---------------------------------------------------- */}
				<div className='mt-4'>
					<div>
						<Button onClick={handleCloseModal} className='buttonStyle__3'>
							{t("organizationManagement.close")}
						</Button>
						<Button
							onClick={onClickSave}
							disabled={disableSaveButton()}
							type='primary'
							className='buttonStyle__3'
						>
							{t("general.save")}
						</Button>
					</div>
				</div>
			</Spin>
		</Drawer>
	);
}

export default BanksEditForm;
